<template>
  <BaseLayout>
    <template v-slot:top_buttons>
      <div class="d-flex justify-content-end mb-2">
        <DocLink docKey="orders" text="Помощь по разделу" />
      </div>
    </template>
    <OffCanvas ref="orderOffCanvas" :title="modalTitle" :width="600">
      <OrderEdit v-if="selectedOrderId"
                 :order-id="selectedOrderId"
                 v-model="orderEditData"
                 @success="orderEditSuccess"/>
    </OffCanvas>
    <div class="card orders-filters-card">
      <div class="card-header" v-if="WindowWidth <= 720" @click="mobileFilterShow = !mobileFilterShow">
        <h5 class="card-action-title mb-0">Фильтр по заказам</h5>
        <div class="card-action-element">
          <ul class="list-inline mb-0">
            <li class="list-inline-item">
              <i class="tf-icons ti scaleX-n1-rtl ti-sm"
                 :class="mobileFilterShow ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
            </li>
          </ul>
        </div>
      </div>
      <div class="card-body orders-filters" :class="{'collapse': WindowWidth <= 720, 'show': mobileFilterShow}">
        <form @submit.prevent="makeSearch">
          <div class="orders-filters-row">
            <div class="orders-filter-item orders-filter-date">
              <label class="orders-filter-label">Дата от</label>
              <DatePicker
                v-model="searchData.creation_date_from"
                class="w-100"
                dateFormat="dd.mm.yy"
                showIcon
                iconDisplay="input"
                inputClass="orders-filter-control"
              />
            </div>
            <div class="orders-filter-item orders-filter-date">
              <label class="orders-filter-label">Дата до</label>
              <DatePicker
                v-model="searchData.creation_date_to"
                class="w-100"
                dateFormat="dd.mm.yy"
                showIcon
                iconDisplay="input"
                inputClass="orders-filter-control"
              />
            </div>
            <div class="orders-filter-item orders-filter-stores">
              <label class="orders-filter-label">Магазины</label>
              <MultiSelect
                v-model="searchData.stores"
                :options="stores"
                optionLabel="name"
                optionValue="id"
                :showToggleAll="true"
                placeholder="Все"
                class="w-100 orders-filter-control"
                display="comma"
              >
                <template #option="slotProps">
                  <div class="d-flex align-content-center">
                    <img :src="slotProps.option.icon" alt="Маркетплейс" width="20px" style="margin-right: 3px">
                    <span>{{ slotProps.option.name }}</span>
                  </div>
                </template>
              </MultiSelect>
            </div>
            <div class="orders-filter-item orders-filter-type">
              <label class="orders-filter-label">Тип</label>
              <MultiSelect
                v-model="searchData.types"
                :options="OrderTypes"
                optionLabel="name"
                optionValue="value"
                :showToggleAll="false"
                placeholder="Все"
                class="w-100 orders-filter-control"
                display="comma"
              />
            </div>
            <div class="orders-filter-item orders-filter-statuses">
              <label class="orders-filter-label">Статусы</label>
              <MultiSelect
                v-model="searchData.statuses"
                :options="ReportStatuses"
                optionLabel="name"
                optionValue="code"
                :showToggleAll="true"
                placeholder="Все"
                class="w-100 orders-filter-control"
                display="comma"
              />
            </div>
            <div class="orders-filter-item orders-filter-search">
              <label class="orders-filter-label">Товар</label>
              <div class="orders-search-wrap">
                <i class="ti ti-search orders-search-icon"></i>
                <input
                  type="text"
                  name="name"
                  class="form-control orders-filter-control orders-search-input"
                  placeholder="Название или артикул"
                  v-model="searchData.name"
                >
              </div>
            </div>
            <div class="orders-filter-item orders-filter-submit">
              <ButtonComponent
                ref="makeSearchButton"
                class="btn btn-primary orders-submit-btn"
                type="submit"
                @click="makeSearch"
              >
                <i class="ti ti-filter me-1"></i>
                Сформировать
              </ButtonComponent>
            </div>
          </div>
        </form>
      </div>
    </div>
    <template v-if="reportTotals">
      <div class="row g-3 mt-1 report-metric-row">
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <i class="ti ti-cash report-metric-icon"></i>
                <span class="report-metric-label">Сумма заказов</span>
              </div>
              <div class="report-metric-value">{{ formatMoney(reportTotals['total_sum']) }}</div>
              <div class="report-metric-caption">выручка по выбранным фильтрам</div>
            </LoadingBlock>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card report-metric-card-accent h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <span class="report-metric-icon-badge">
                  <i class="ti ti-trending-up"></i>
                </span>
                <span class="report-metric-label">Прибыль</span>
              </div>
              <div class="report-metric-value report-metric-value-accent">
                {{ formatMoney(reportTotals['total_profit']) }}
              </div>
              <div class="report-metric-caption">
                {{ formatNumber(reportTotals['profit_percent'], 1) }}% от выручки
                ·
                {{ formatNumber(reportTotals['profit_percent_cost'], 1) }}% от себестоимости
              </div>
            </LoadingBlock>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <i class="ti ti-package report-metric-icon"></i>
                <span class="report-metric-label">Товаров</span>
              </div>
              <div class="report-metric-value">{{ formatNumber(reportTotals['products_count']) }}</div>
              <div class="report-metric-caption">
                средний чек {{ formatMoney(reportTotals['average_price']) }}
              </div>
            </LoadingBlock>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <i class="ti ti-box report-metric-icon"></i>
                <span class="report-metric-label">Себестоимость</span>
              </div>
              <div class="report-metric-value">{{ formatMoney(reportTotals['total_cost']) }}</div>
              <div class="report-metric-caption">
                {{ percentOfSum(reportTotals['total_cost']) }}% от суммы заказов
              </div>
            </LoadingBlock>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <i class="ti ti-receipt-2 report-metric-icon"></i>
                <span class="report-metric-label">Комиссия</span>
              </div>
              <div class="report-metric-value">{{ formatMoney(reportTotals['total_commission']) }}</div>
              <div class="report-metric-caption">
                {{ percentOfSum(reportTotals['total_commission']) }}% от суммы заказов
              </div>
            </LoadingBlock>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-sm-6">
          <div class="report-metric-card h-100">
            <LoadingBlock :loading="loadingReport">
              <div class="report-metric-head">
                <i class="ti ti-file-invoice report-metric-icon"></i>
                <span class="report-metric-label">Налоги</span>
              </div>
              <div class="report-metric-value">{{ formatMoney(reportTotals['total_tax']) }}</div>
              <div class="report-metric-caption">
                {{ percentOfSum(reportTotals['total_tax']) }}% от суммы заказов
              </div>
            </LoadingBlock>
          </div>
        </div>
      </div>
      <p class="report-metrics-note">
        * Комиссия, налоги и прибыль — прогнозные значения, рассчитываются автоматически и могут отличаться от фактических начислений маркетплейса.
      </p>
    </template>
    <div class="card summary-table-card">
      <div class="summary-table-header">
        <h5 class="summary-table-title mb-0">Сводная таблица</h5>
        <div class="summary-group-control">
          <span class="orders-filter-label mb-0">Группировать</span>
          <Select
            v-model="searchData.group_by"
            :options="groupOptions"
            optionLabel="name"
            optionValue="code"
            class="summary-group-select"
            @update:modelValue="onGroupByChange"
          />
        </div>
      </div>

      <div class="card-body pt-0">
        <LoadingBlock :loading="loadingReport">
          <div class="table-responsive">
            <table class="table summary-table mb-0">
              <thead>
              <tr>
                <th class="summary-group-col">{{ getGroupName(searchData.group_by) }}</th>
                <th class="text-end">Заказы</th>
                <th class="text-end">Сумма</th>
                <th class="text-end">Себестоимость</th>
                <th class="text-end">Комиссия</th>
                <th class="text-end">Налоги</th>
                <th class="text-end">Прибыль</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in reportRows" :key="index">
                <td class="summary-group-col">{{ formatGroup(row.group) }}</td>
                <td class="text-end">{{ row.products_count }}</td>
                <td class="text-end">{{ formatMoney(row.total_sum) }}</td>
                <td class="text-end">{{ formatMoney(row.total_cost) }}</td>
                <td class="text-end">{{ formatMoney(row.total_commission) }}</td>
                <td class="text-end">{{ formatMoney(row.total_tax) }}</td>
                <td class="text-end">
                  <div class="fw-semibold">{{ formatMoney(row.total_profit) }}</div>
                  <div class="summary-profit-percents">
                    {{ formatNumber(row.profit_percent, 1) }}% от выручки
                  </div>
                  <div class="summary-profit-percents">
                    {{ formatNumber(row.profit_percent_cost, 1) }}% от себестоимости
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </LoadingBlock>
      </div>
    </div>


    <div class="card orders-list-card mt-6">
      <div class="orders-list-header">
        <h5 class="orders-list-title mb-0">Заказы</h5>
        <div class="orders-list-count" v-if="paginationInfo.total !== undefined">
          {{ orders.length }} из {{ paginationInfo.total }}
        </div>
      </div>

      <div class="card-body pt-0">
        <LoadingBlock :loading="loadingOrders">
          <div class="table-responsive" v-if="WindowWidth > 720">
            <table class="table orders-table mb-0">
              <thead>
              <tr>
                <th>Детали</th>
                <th>Артикул, название</th>
                <th class="text-end">Сумма</th>
                <th class="text-end">Себестоимость</th>
                <th class="text-end">Комиссия</th>
                <th class="text-end">Налоги</th>
                <th class="text-end">Прибыль</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in orders" :key="row.id || index">
                <td class="orders-details-col">
                  <div class="orders-details-date">
                    {{ formatOrderDate(row.creation_date) }}
                    <a href="javascript:void(0)" @click="openOrderEdit(row)" class="orders-edit-link" title="Редактировать">
                      <i class="ti ti-edit"></i>
                    </a>
                  </div>
                  <div class="orders-details-meta">
                    <img :src="getMarketplaceIcon(row.marketplace)" width="20" height="20" alt="Маркетплейс">
                    <span class="badge rounded-pill" :class="getStatusBadge(row.status)">
                      {{ getStatusName(row.status) }}
                    </span>
                  </div>
                  <div class="orders-details-number">{{ row.marketplace_number }}</div>
                </td>
                <td class="orders-product-col">
                  <template v-if="row.store_url">
                    <div class="orders-product-code">
                      <a :href="row.store_url" target="_blank" rel="noopener noreferrer"
                         class="product-link" title="Открыть на маркетплейсе">
                        {{ row.product_code }}
                      </a>, {{ row.quantity }} шт
                    </div>
                    <a :href="row.store_url" target="_blank" rel="noopener noreferrer"
                       class="product-link orders-product-name" title="Открыть на маркетплейсе">
                      {{ row.product_current_name }}
                    </a>
                  </template>
                  <template v-else>
                    <div class="orders-product-code">{{ row.product_code }}, {{ row.quantity }} шт</div>
                    <div class="orders-product-name">{{ row.product_current_name }}</div>
                  </template>
                </td>
                <td class="text-end">{{ formatNumber(row.total_sum) }}</td>
                <td class="text-end">{{ formatNumber(row.total_cost) }}</td>
                <td class="text-end">
                  <div class="order-commission-cell">
                    <span>{{ formatNumber(row.total_commission) }}</span>
                    <span v-if="row.commission_info" class="order-commission-tooltip-wrap">
                      <i class="ti ti-info-circle ti-sm ms-1 order-commission-tooltip-icon"></i>
                      <div class="order-commission-tooltip">
                        <CommissionTooltip
                          :commissions-data="row.commission_info"
                          :tooltip-width="560"
                          :tooltip-max-width="560"
                        />
                      </div>
                    </span>
                  </div>
                </td>
                <td class="text-end">{{ formatNumber(row.total_tax) }}</td>
                <td class="text-end orders-profit-col">
                  <div class="orders-profit-value">{{ formatMoney(row.total_profit) }}</div>
                  <div class="orders-profit-badges">
                    <span class="badge rounded-pill" :class="row.profit_percent >= 0 ? 'bg-label-success' : 'bg-label-danger'">
                      {{ formatNumber(row.profit_percent, 1) }}% от цены
                    </span>
                    <span class="badge rounded-pill bg-label-secondary">
                      {{ formatNumber(row.profit_percent_cost, 1) }}% от себест.
                    </span>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="orders-mobile-list">
            <div
              v-for="(row, index) in orders"
              :key="row.id || index"
              class="orders-mobile-card"
            >
              <div class="orders-details-date">
                {{ formatOrderDate(row.creation_date) }}
                <span class="badge rounded-pill ms-1" :class="getStatusBadge(row.status)">
                  {{ getStatusName(row.status) }}
                </span>
                <a href="javascript:void(0)" @click="openOrderEdit(row)" class="orders-edit-link ms-1" title="Редактировать">
                  <i class="ti ti-edit"></i>
                </a>
              </div>

              <div class="orders-product-col mt-2">
                <template v-if="row.store_url">
                  <div class="orders-product-code">
                    <a :href="row.store_url" target="_blank" rel="noopener noreferrer"
                       class="product-link" title="Открыть на маркетплейсе">
                      {{ row.product_code }}
                    </a>, {{ row.quantity }} шт
                  </div>
                  <a :href="row.store_url" target="_blank" rel="noopener noreferrer"
                     class="product-link orders-product-name" title="Открыть на маркетплейсе">
                    {{ row.product_current_name }}
                  </a>
                </template>
                <template v-else>
                  <div class="orders-product-code">{{ row.product_code }}, {{ row.quantity }} шт</div>
                  <div class="orders-product-name">{{ row.product_current_name }}</div>
                </template>
              </div>

              <table class="w-100 mt-3 orders-mobile-metrics">
                <tbody>
                <tr>
                  <td>Сумма</td>
                  <td class="text-end">{{ formatMoney(row.total_sum) }}</td>
                </tr>
                <tr>
                  <td>Себестоимость</td>
                  <td class="text-end">{{ formatMoney(row.total_cost) }}</td>
                </tr>
                <tr>
                  <td>Комиссия</td>
                  <td class="text-end">
                    <div class="order-commission-cell order-commission-cell-mobile">
                      <span>{{ formatMoney(row.total_commission) }}</span>
                      <span v-if="row.commission_info" class="order-commission-tooltip-wrap">
                        <i class="ti ti-info-circle ti-sm ms-1 order-commission-tooltip-icon"></i>
                        <div class="order-commission-tooltip">
                          <CommissionTooltip
                            :commissions-data="row.commission_info"
                            :tooltip-min-width="320"
                            :tooltip-max-width="560"
                          />
                        </div>
                      </span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Налоги</td>
                  <td class="text-end">{{ formatMoney(row.total_tax) }}</td>
                </tr>
                <tr>
                  <td>Прибыль</td>
                  <td class="text-end">
                    <div class="orders-profit-value">{{ formatMoney(row.total_profit) }}</div>
                    <div class="orders-profit-badges justify-content-end">
                      <span class="badge rounded-pill" :class="row.profit_percent >= 0 ? 'bg-label-success' : 'bg-label-danger'">
                        {{ formatNumber(row.profit_percent, 1) }}% от цены
                      </span>
                      <span class="badge rounded-pill bg-label-secondary">
                        {{ formatNumber(row.profit_percent_cost, 1) }}% от себест.
                      </span>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>

              <div class="orders-details-meta mt-3 mb-0">
                <img :src="getMarketplaceIcon(row.marketplace)" width="20" height="20" alt="Маркетплейс">
                <span class="orders-details-number mb-0">{{ row.marketplace_number }}</span>
              </div>
            </div>
          </div>

          <BasePagination :info="paginationInfo" ref="productsPagination" @changePage="changePage">
          </BasePagination>
        </LoadingBlock>
      </div>
    </div>


  </BaseLayout>
</template>

<script>
import BaseLayout from '@/layouts/Base.vue'
import {PageInfo} from "@/data/page";
import {user} from "@/data/user";
import {subscription} from "@/data/subscription";
import router from "@/router";
import {getMarketplaceIcon, Marketplaces, stores} from "@/data/store";
import BasePagination from "@/components/list/Pagination.vue"

import {ReportGroupOptions, OrderTypes, ReportStatuses} from "@/data/orders.js"

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {getOrdersCalculated, getReportV1} from "@/api/order";
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import MultiSelect from "primevue/multiselect";
import {DatePicker, Select} from "primevue";
import ButtonComponent from "@/components/tools/Button.vue";
import {Message, Messages} from "@/data/messages";
import OffCanvas from "@/components/page/OffCanvas.vue";
import OrderEdit from "@/components/forms/OrderEdit.vue";
import DocLink from "@/components/tools/DocLink.vue";
import CommissionTooltip from "@/components/price/Commission.vue";


export default {
  name: 'ReportV1View',
  computed: {
    OrderTypes() {
      return OrderTypes
    },
    ReportStatuses() {
      return ReportStatuses
    },
    WindowWidth() {
      return window.innerWidth
    }
  },
  // eslint-disable-next-line vue/no-unused-components
  components: {
    ButtonComponent,
    LoadingBlock,
    BaseLayout,
    BasePagination,
    MultiSelect,
    DatePicker,
    Select,
    OffCanvas,
    OrderEdit,
    DocLink,
    CommissionTooltip
  },
  data() {
    return {
      currency: '₽',
      groupOptions: ReportGroupOptions,
      stores: [],
      paginationInfo: {
        page: 1,
        size: 50
      },
      searchData: {
        company: '',
        name: '',
        creation_date_from: new Date(),
        creation_date_to: new Date(),
        types: [],
        stores: [],
        statuses: ['processing', 'delivery', 'delivered'],
        marketplaces: [],
        group_by: 'store'
      },
      reportRows: [],
      reportTotals: {},
      orders: [],
      loadingReport: true,
      loadingOrders: true,
      mobileFilterShow: false,
      selectedOrderId: '',
      orderEditData: { price: 0, cost: 0, commission: 0, tax: 0 },
      modalTitle: 'Редактировать заказ'
    }
  },
  methods: {
    getMarketplaceIcon,
    setPageInfo() {
      PageInfo.title = 'Отчет по заказам'
      PageInfo.breadcrumbs = [
        {
          title: 'Главная',
          link: '/'
        },
        {
          title: PageInfo.title
        }
      ]
    },
    async setSearch() {
      this.searchData.company = await user.getActiveCompany()
    },
    async makeSearch() {
      this.loadingReport = true
      this.loadingOrders = true
      this.$refs.makeSearchButton.startLoading()
      await this.makeReport()
      this.loadingReport = false
      await this.searchOrders()
      this.loadingOrders = false
      this.$refs.makeSearchButton.endLoading()
    },
    async makeReport() {
      try {
        let result = await getReportV1(this.searchData)
        this.reportRows = result.data.rows
        this.reportTotals = result.data.totals
      } catch (error) {
        this.serverError()
      }
    },
    async searchOrders() {
      try {
        let result = await getOrdersCalculated(this.searchData, this.paginationInfo.page, this.paginationInfo.size)
        this.orders = result.data.items
        this.paginationInfo = {
          total: result.data.total,
          pages: result.data.pages,
          page: result.data.page,
          size: result.data.size
        }
      } catch (error) {
        this.serverError()
      }
    },
    serverError(text = '') {
      const message = new Message()
      if (!text.length) {
        text = 'Ошибка на стороне сервера, попробуйте позже или обратитесь в поддержку'
      }
      message.text = text
      message.type = 'failure'
      message.format = 'notification'
      Messages.list.push(message)
    },
    formatNumber(value, to_fixed = 0) {
      if (!value) return '0';
      value = Number(value).toFixed(to_fixed)
      return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
    },
    formatMoney(value, to_fixed = 0) {
      return this.formatNumber(value, to_fixed) + ' ' + this.currency
    },
    percentOfSum(value) {
      const total = Number(this.reportTotals?.total_sum) || 0
      const amount = Number(value) || 0
      if (!total) return '0.0'
      return (amount / total * 100).toFixed(1)
    },
    async onGroupByChange() {
      this.loadingReport = true
      await this.makeReport()
      this.loadingReport = false
    },
    formatGroup(group) {
      if (this.searchData.group_by === 'marketplace') {
        const marketplace = Marketplaces[group]
        if (typeof marketplace !== 'undefined') {
          return marketplace.name
        }
      }
      return group
    },
    getStatusName(status_code) {
      const status = ReportStatuses.find((status) => status.code === status_code)
      if (typeof status !== 'undefined') {
        return status.name
      }
      return status_code
    },
    async changePage(page) {
      this.paginationInfo.page = page
      await this.searchOrders()
    },
    getGroupName(group_code) {
      const labels = {
        day: 'Дата',
        week: 'Неделя',
        month: 'Месяц',
        year: 'Год',
        product: 'Товар',
        brand: 'Бренд',
        store: 'Магазин',
        marketplace: 'Маркетплейс'
      }
      return labels[group_code] || ReportGroupOptions.find((group) => group.code === group_code)?.name || group_code
    },
    formatOrderDate(date_string) {
      const date = new Date(date_string);
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}.${month}.${year} ${hours}:${minutes}`
    },
    getStatusBadge(status_code) {
      if (status_code === 'cancelled' || status_code === 'returned') {
        return 'bg-label-danger'
      }
      if (status_code === 'processing') {
        return 'bg-label-primary'
      }
      if (status_code === 'delivered') {
        return 'bg-label-success'
      }
      if (status_code === 'delivery') {
        return 'bg-label-warning'
      }
      return 'bg-label-info'
    },
    openOrderEdit(row) {
      this.selectedOrderId = row.id
      this.orderEditData = {
        price: row.total_sum || 0,
        cost: row.total_cost || 0,
        commission: row.total_commission || 0,
        tax: row.total_tax || 0
      }
      this.$refs.orderOffCanvas.open()
    },
    async orderEditSuccess() {
      this.$refs.orderOffCanvas.close()
      await this.makeReport()
      await this.searchOrders()
    }
  },
  async mounted() {
    this.setPageInfo()
    await user.getUserBase()
    
    // Проверка активности тарифа
    const isActive = await subscription.isActive()
    if (!isActive) {
      await router.push({ name: 'payment', query: { error: 'expired' } })
      return
    }
    
    await this.setSearch()
    this.stores = await stores.getStores()
    await this.makeSearch()
  },
  watch: {
    async '$route'() {
      this.setPageInfo()
      await this.setSearch()
    }
  },
}
</script>

<style>
.orders-filters-card {
  border: 0;
  box-shadow: 0 2px 12px rgba(47, 43, 61, 0.06);
  border-radius: 0.75rem;
}

.orders-filters {
  padding: 1.25rem 1.5rem;
}

.orders-filter-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a8a3b5;
}

.orders-filters-row {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: end;
}

@media (min-width: 992px) {
  .orders-filters-row {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .orders-filter-submit {
    grid-column: 1 / -1;
    justify-self: start;
  }
}

@media (min-width: 1400px) {
  .orders-filters-row {
    gap: 0.85rem;
  }
}

.orders-search-wrap {
  position: relative;
}

.orders-search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a8a3b5;
  pointer-events: none;
  z-index: 1;
}

.orders-search-input {
  padding-left: 2.25rem !important;
}

.orders-submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-height: 38px;
  border-radius: 0.5rem !important;
  padding-left: 0.9rem;
  padding-right: 0.9rem;
}

.orders-filters .orders-filter-control,
.orders-filters .p-datepicker,
.orders-filters .p-multiselect,
.orders-filters .p-select,
.orders-filters .form-control {
  width: 100%;
  min-height: 38px;
  border-radius: 0.5rem;
}

.orders-filters .p-datepicker-input,
.orders-filters .p-multiselect,
.orders-filters .p-select,
.orders-filters .form-control {
  min-height: 38px;
  border-radius: 0.5rem;
  border-color: #e0dce8;
  font-size: 0.875rem;
}

.orders-filters .p-multiselect .p-multiselect-label,
.orders-filters .p-select .p-select-label {
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
}

/* Clear default inset-inline-end so the icon box doesn't stretch over the input. */
.orders-filters .p-datepicker-input-icon-container {
  inset-inline-start: 0.55rem;
  inset-inline-end: auto;
  z-index: 1;
}

.orders-filters .p-datepicker:has(.p-datepicker-input-icon-container) .p-datepicker-input {
  padding-inline-start: 2rem;
  padding-inline-end: 0.75rem;
}

.report-metric-row {
  margin-top: 1rem !important;
}

.report-metric-card {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  box-shadow: 0 2px 12px rgba(47, 43, 61, 0.04);
  padding: 1rem 1.1rem 1.05rem;
}

.report-metric-card-accent {
  border-color: #7367f0;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.12);
}

.report-metric-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.7rem;
}

.report-metric-icon {
  color: #7367f0;
  font-size: 1.05rem;
  line-height: 1;
}

.report-metric-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.45rem;
  height: 1.45rem;
  border-radius: 999px;
  background: #7367f0;
  color: #fff;
  font-size: 0.85rem;
}

.report-metric-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #5d596c;
}

.report-metric-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
  color: #2f2b3d;
  margin-bottom: 0.35rem;
}

.report-metric-value-accent {
  color: #7367f0;
}

.report-metric-caption {
  font-size: 0.75rem;
  line-height: 1.35;
  color: #6f6b7d;
}

.report-metrics-note {
  margin: 1.75rem 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #6f6b7d;
  text-align: left;
}

.summary-table-card {
  border: 0;
  box-shadow: 0 2px 12px rgba(47, 43, 61, 0.06);
  border-radius: 0.75rem;
}

.summary-table-card > .card-body,
.orders-list-card > .card-body {
  padding-left: 0;
  padding-right: 0;
}

.summary-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  flex-wrap: wrap;
}

.summary-table-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2f2b3d;
}

.summary-group-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.summary-group-select {
  min-width: 160px;
}

.summary-group-select.p-select {
  min-height: 38px;
  border-radius: 0.5rem;
  border-color: #e0dce8;
}

.summary-table {
  --bs-table-bg: transparent;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.summary-table thead th {
  border: 0;
  border-bottom: 1px solid #ebe9f1;
  padding: 0.75rem 1rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a8a3b5;
  white-space: nowrap;
  background: transparent;
}

.summary-table tbody td {
  border: 0;
  border-bottom: 1px solid #f0eef5;
  padding: 0.9rem 1rem;
  color: #4b465c;
  white-space: nowrap;
  vertical-align: middle;
}

.summary-table thead th:first-child,
.summary-table tbody td:first-child {
  padding-left: 1.5rem;
}

.summary-table thead th:last-child,
.summary-table tbody td:last-child {
  padding-right: 1.5rem;
}

.summary-group-col {
  max-width: 280px;
  min-width: 160px;
  width: 22%;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.summary-table tbody tr:last-child td {
  border-bottom: 0;
}

.summary-profit-percents {
  margin-top: 0.2rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6f6b7d;
  white-space: normal;
}

.orders-list-card {
  border: 0;
  box-shadow: 0 2px 12px rgba(47, 43, 61, 0.06);
  border-radius: 0.75rem;
}

.orders-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
}

.orders-list-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2f2b3d;
}

.orders-list-count {
  font-size: 0.875rem;
  color: #6f6b7d;
}

.orders-table {
  --bs-table-bg: transparent;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.orders-table thead th {
  border: 0;
  border-bottom: 1px solid #ebe9f1;
  padding: 0.75rem 1rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6f6b7d;
  white-space: nowrap;
  background: transparent;
}

.orders-table tbody td {
  border: 0;
  border-bottom: 1px solid #f0eef5;
  padding: 0.95rem 1rem;
  color: #4b465c;
  vertical-align: top;
}

.orders-table thead th:first-child,
.orders-table tbody td:first-child {
  padding-left: 1.5rem;
}

.orders-table thead th:last-child,
.orders-table tbody td:last-child {
  padding-right: 1.5rem;
}

.orders-table tbody tr:last-child td {
  border-bottom: 0;
}

.orders-details-col {
  min-width: 180px;
  max-width: 240px;
}

.orders-details-date {
  color: #2f2b3d;
  font-weight: 500;
  margin-bottom: 0.4rem;
}

.orders-edit-link {
  color: #6f6b7d;
  margin-left: 0.35rem;
}

.orders-edit-link:hover {
  color: #7367f0;
}

.orders-details-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
}

.orders-details-number {
  font-size: 0.8125rem;
  color: #6f6b7d;
}

.orders-product-col {
  min-width: 220px;
  max-width: 340px;
  white-space: normal;
}

.orders-product-code {
  color: #2f2b3d;
  font-weight: 500;
  margin-bottom: 0.2rem;
}

.orders-product-name {
  display: block;
  font-size: 0.8125rem;
  color: #6f6b7d;
  line-height: 1.35;
}

.orders-profit-col {
  min-width: 150px;
}

.orders-profit-value {
  font-weight: 700;
  color: #2f2b3d;
  margin-bottom: 0.35rem;
}

.orders-profit-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.orders-profit-badges .badge {
  font-weight: 600;
}

.orders-profit-badges .bg-label-success {
  color: #1f9d57 !important;
}

.orders-profit-badges .bg-label-danger {
  color: #d4380d !important;
}

.orders-profit-badges .bg-label-secondary {
  color: #4b465c !important;
}

.orders-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0 1.5rem;
}

.orders-mobile-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  padding: 1rem;
  background: #fff;
}

.orders-mobile-metrics td {
  padding: 0.35rem 0;
  color: #6f6b7d;
  font-size: 0.875rem;
}

.orders-mobile-metrics td.text-end {
  color: #2f2b3d;
  font-weight: 500;
}

.product-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.product-link:hover {
  color: #7367f0;
}

.order-commission-cell {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
}

.order-commission-cell-mobile {
  width: 100%;
}

.order-commission-tooltip-wrap {
  position: relative;
  display: inline-flex;
  padding-bottom: 8px;
  margin-bottom: -8px;
}

.order-commission-tooltip-icon {
  color: #007bff;
}

.order-commission-tooltip {
  display: none;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 3000;
}

.order-commission-tooltip-wrap:hover .order-commission-tooltip {
  display: block;
}

.order-commission-tooltip .popover {
  max-width: 560px;
  --bs-popover-max-width: 560px;
}
</style>